import React, { useState, useEffect, useMemo } from 'react';
import {
  LuBuilding2, LuSearch, LuPhone, LuMail, LuMapPin, LuUser,
  LuUsers, LuBookOpen, LuHeartHandshake, LuGraduationCap,
  LuWallet, LuShield, LuRadio, LuArrowRight,
  LuX, LuSprout, LuLoader, LuExternalLink
} from 'react-icons/lu';
import { GiHolySymbol } from 'react-icons/gi';
import { FaWhatsapp, FaTelegramPlane } from 'react-icons/fa';
import departmentsHero from '../../assets/deptof.png';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../shared/firebase';

// Helper to format field keys into clean title case labels
const formatFieldLabel = (key = '') => {
  if (!key) return '';
  const overrides = {
    asst: 'Assistant',
    dtp: 'DTP',
    mob: 'Mobile',
    phoneno: 'Phone',
    phone_number: 'Phone Number',
    office_contact_no: 'Office Contact',
    'office_assistant_&_dtp': 'Office Assistant & DTP',
    office_assistant: 'Office Assistant',
    chief_editor: 'Chief Editor',
    preistName: 'Priest Name',
    department_name: 'Department Name',
    convenor: 'Convenor',
    secretary: 'Secretary',
    details: 'Details & In-Charge',
    bishops_secretary: "Bishop's Secretary",
    bishops_chaplain: "Bishop's Chaplain",
    bishops_driver: "Bishop's Driver",
    senior_clerk: 'Senior Clerk',
    driver_cum_attender: 'Driver Cum Attender',
    whatsapp: 'WhatsApp',
    telegram: 'Telegram'
  };
  if (overrides[key]) return overrides[key];

  return key
    .replace(/_/g, ' ')
    .replace(/&/g, ' & ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map(word => {
      const w = word.toLowerCase();
      if (w === 'dtp') return 'DTP';
      if (w === 'pr') return 'PR';
      if (w === 'csi') return 'CSI';
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
};

// Helper to safely extract string value from map { order, value } or direct value
const extractFieldValue = (field) => {
  if (!field) return '';
  if (typeof field === 'object' && field !== null) {
    if (field.value !== undefined && field.value !== null) return String(field.value).trim();
    if (field.name !== undefined) return String(field.name).trim();
  }
  if (typeof field === 'string' || typeof field === 'number') {
    return String(field).trim();
  }
  return '';
};

// Helper to extract inner order from map { order, value }
const extractFieldOrder = (field, defaultOrder = 99) => {
  if (field && typeof field === 'object' && typeof field.order === 'number') {
    return field.order;
  }
  return defaultOrder;
};

// Helper to assign thematic icons based on department title or category
const getDepartmentIcon = (title = '', category = '') => {
  const t = (title + ' ' + category).toLowerCase();
  if (t.includes('bishop') || t.includes('chaplain') || t.includes('holy')) return GiHolySymbol;
  if (t.includes('account') || t.includes('finance') || t.includes('treasury')) return LuWallet;
  if (t.includes('property') || t.includes('estate') || t.includes('building')) return LuBuilding2;
  if (t.includes('communication') || t.includes('media') || t.includes('radio') || t.includes('sabhamithram') || t.includes('dtp')) return LuRadio;
  if (t.includes('women') || t.includes('fellowship') || t.includes('prayer')) return LuHeartHandshake;
  if (t.includes('youth') || t.includes('counseling') || t.includes('team')) return LuUsers;
  if (t.includes('school') || t.includes('education') || t.includes('college')) return LuGraduationCap;
  if (t.includes('ecology') || t.includes('nature') || t.includes('green')) return LuSprout;
  if (t.includes('care') || t.includes('sadanam') || t.includes('welfare') || t.includes('child')) return LuShield;
  if (t.includes('book') || t.includes('publication')) return LuBookOpen;
  return LuBuilding2;
};

// Helper to extract phone number digits for tel: links
const extractPhoneNumber = (text = '') => {
  if (!text) return '';
  const match = text.match(/(?:(?:ph|mob|phone|mobile|tel)?[:\s-]*)?(\+?[0-9]{2,5}[-\s]?[0-9]{6,10})/i);
  if (match && match[1]) {
    return match[1].replace(/[^0-9+]/g, '');
  }
  const digitsOnly = text.replace(/[^0-9+]/g, '');
  return digitsOnly.length >= 7 ? digitsOnly : '';
};

export default function OurDepartments({ activeSection = 'departments', onSelectSection = () => { } }) {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch departments from Firestore
  useEffect(() => {
    let isMounted = true;
    async function fetchDepartments() {
      try {
        setLoading(true);
        let querySnapshot = await getDocs(collection(db, 'departments'));
        if (querySnapshot.empty) {
          const altSnapshot = await getDocs(collection(db, 'departmenets'));
          if (!altSnapshot.empty) {
            querySnapshot = altSnapshot;
          }
        }

        const fetched = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();

          // 1. Department Title
          const title = extractFieldValue(data.department_name) || data.title || data.name || 'Department';

          // 2. Department Sorting Order
          let docOrder = 999;
          if (typeof data.order === 'number') {
            docOrder = data.order;
          } else if (data.order && typeof data.order === 'object' && typeof data.order.value === 'number') {
            docOrder = data.order.value;
          }

          // 3. Category / List Heading
          let listHeading = data.listHeading;
          if (!listHeading || listHeading.trim() === '') {
            listHeading = title.toLowerCase().includes('bishop') ? 'Administration' : 'Ministries';
          } else {
            listHeading = listHeading.trim();
          }

          // 4. Team Array
          const team = Array.isArray(data.team) ? data.team : [];

          // 5. Parse all dynamic map fields
          const dynamicFields = [];
          const ignoredKeys = new Set(['department_name', 'title', 'name', 'order', 'listHeading', 'team', 'id']);

          let whatsappVal = '';
          let telegramVal = '';

          Object.keys(data).forEach((key) => {
            if (ignoredKeys.has(key)) return;

            const val = extractFieldValue(data[key]);
            if (!val || val === '.' || val === '-') return; // Skip placeholder values

            const innerOrder = extractFieldOrder(data[key], 99);
            const label = formatFieldLabel(key);
            const lowerKey = key.toLowerCase();

            if (lowerKey === 'whatsapp') {
              whatsappVal = val;
              return;
            }
            if (lowerKey === 'telegram') {
              telegramVal = val;
              return;
            }

            const isEmail = lowerKey.includes('email') || (val.includes('@') && !val.includes(' '));
            const isPhone = lowerKey.includes('phone') || lowerKey.includes('mob') || lowerKey.includes('contact_no') || lowerKey.includes('tel');
            const isAddress = lowerKey.includes('address') || lowerKey.includes('location');

            dynamicFields.push({
              key,
              label,
              value: val,
              order: innerOrder,
              isEmail,
              isPhone,
              isAddress,
              phoneNum: isPhone ? extractPhoneNumber(val) : ''
            });
          });

          // Sort dynamic fields by defined order, keeping driver fields at the very end
          dynamicFields.sort((a, b) => {
            const aIsDriver = a.key.toLowerCase().includes('driver') || a.label.toLowerCase().includes('driver');
            const bIsDriver = b.key.toLowerCase().includes('driver') || b.label.toLowerCase().includes('driver');
            if (aIsDriver && !bIsDriver) return 1;
            if (!aIsDriver && bIsDriver) return -1;
            return a.order - b.order;
          });

          // Extract quick contacts for card footer
          const emailField = dynamicFields.find(f => f.isEmail);
          const phoneField = dynamicFields.find(f => f.isPhone || extractPhoneNumber(f.value));
          const addressField = dynamicFields.find(f => f.isAddress);

          fetched.push({
            id: doc.id,
            title,
            category: listHeading,
            badge: listHeading !== 'Ministries' ? listHeading : 'Diocesan Ministry',
            order: docOrder,
            team,
            dynamicFields,
            whatsapp: whatsappVal,
            telegram: telegramVal,
            email: emailField ? emailField.value : '',
            phone: phoneField ? phoneField.value : '',
            phoneNumber: phoneField ? (phoneField.phoneNum || extractPhoneNumber(phoneField.value)) : '',
            address: addressField ? addressField.value : '',
            icon: getDepartmentIcon(title, listHeading)
          });
        });

        // Sort departments by overall order ascending
        fetched.sort((a, b) => {
          if (a.order !== b.order) return a.order - b.order;
          return a.title.localeCompare(b.title);
        });

        if (isMounted) {
          setDepartments(fetched);
        }
      } catch (error) {
        console.error("Error fetching departments from Firebase:", error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    fetchDepartments();
    return () => { isMounted = false; };
  }, []);

  // Extract dynamic categories from departments data
  const categories = useMemo(() => {
    const map = new Map();
    departments.forEach((d) => {
      const cat = d.category || 'Ministries';
      map.set(cat, (map.get(cat) || 0) + 1);
    });

    const list = [
      { id: 'all', label: 'All Ministries', count: departments.length }
    ];

    map.forEach((count, label) => {
      list.push({
        id: label,
        label: label,
        count: count
      });
    });

    return list;
  }, [departments]);

  // Filter & Search Logic
  const filteredDepartments = useMemo(() => {
    return departments.filter(dept => {
      const matchesCategory = selectedCategory === 'all' || dept.category === selectedCategory;
      if (!matchesCategory) return false;
      if (!searchQuery.trim()) return true;

      const q = searchQuery.toLowerCase();
      const inTitle = dept.title.toLowerCase().includes(q);
      const inCategory = dept.category.toLowerCase().includes(q);
      const inFields = dept.dynamicFields.some(f =>
        f.label.toLowerCase().includes(q) || f.value.toLowerCase().includes(q)
      );
      const inTeam = dept.team.some(t => t.toLowerCase().includes(q));
      const inSocial = (dept.whatsapp && dept.whatsapp.includes(q)) || (dept.telegram && dept.telegram.includes(q));

      return inTitle || inCategory || inFields || inTeam || inSocial;
    });
  }, [selectedCategory, searchQuery, departments]);

  const totalCount = departments.length;

  // Separate featured Bishop's Office from other standard cards for optimal layout
  const featuredDept = useMemo(() => {
    if (selectedCategory !== 'all' && selectedCategory !== 'Administration') return null;
    if (searchQuery.trim()) return null; // When searching, display in regular grid
    return filteredDepartments.find(d => d.title.toLowerCase().includes('bishop') || d.order === 1);
  }, [filteredDepartments, selectedCategory, searchQuery]);

  const standardDepartments = useMemo(() => {
    if (!featuredDept) return filteredDepartments;
    return filteredDepartments.filter(d => d.id !== featuredDept.id);
  }, [filteredDepartments, featuredDept]);

  return (
    <div className="min-h-screen bg-[#FAF7F2] text-[#1A1A1A] font-sans selection:bg-[#842343]/20 selection:text-[#842343] pb-16">

      {/* =========================================================
          1. STANDARDIZED HERO HEADER BANNER
      ========================================================= */}
      <section className="relative w-full overflow-hidden min-h-[380px] sm:min-h-[420px] lg:min-h-[440px] flex items-center">
        <div className="absolute inset-0 z-0">
          <img
            src={departmentsHero}
            alt="Diocesan Departments & Ministries"
            className="w-full h-full object-cover object-[right_center] filter brightness-[0.75] contrast-[1.08]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#140e1b]/90 via-[#140e1b]/40 to-black/30"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-black/40"></div>
        </div>

        <div className="relative z-10 max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 w-full pt-28 sm:pt-32 pb-16 flex flex-col lg:flex-row items-start lg:items-end justify-between gap-8">
          
          <div className="max-w-xl text-left">
            <h1 className="font-serif font-bold text-white text-[38px] sm:text-[48px] lg:text-[56px] tracking-[0.04em] uppercase leading-[1.05] drop-shadow-md">
              DEPARTMENTS &amp; MINISTRIES
            </h1>
            <p className="text-[#E8B86D] text-[15px] sm:text-[17px] font-medium tracking-wide mt-2.5 drop-shadow-sm">
              Explore the administrative offices, pastoral fellowships, publication wings, and community welfare departments serving the CSI Diocese of Malabar.
            </p>
            <div className="flex items-center gap-2 text-xs text-white/80 font-medium mt-4">
              <span onClick={() => onSelectSection('home')} className="hover:text-white cursor-pointer transition-colors">Home</span>
              <span className="text-[#E8B86D] font-bold">›</span>
              <span className="text-white font-semibold">Departments &amp; Ministries</span>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 sm:gap-4 w-full sm:w-auto">
            <div className="flex items-center gap-3 bg-[#7B1E3A]/90 hover:bg-[#7B1E3A] backdrop-blur-md text-white rounded-2xl px-4 py-3 border border-white/15 shadow-[0_8px_25px_rgba(0,0,0,0.3)] transition-all">
              <LuBuilding2 className="w-6 h-6 text-[#E8B86D]" />
              <div>
                <span className="font-serif font-bold text-white text-lg leading-none block">
                  {totalCount > 0 ? `${totalCount}` : '15+'}
                </span>
                <span className="text-[11px] uppercase tracking-wider text-white/80 font-bold">Departments</span>
              </div>
            </div>

            <div className="flex items-center gap-3 bg-[#7B1E3A]/90 hover:bg-[#7B1E3A] backdrop-blur-md text-white rounded-2xl px-4 py-3 border border-white/15 shadow-[0_8px_25px_rgba(0,0,0,0.3)] transition-all">
              <LuUsers className="w-6 h-6 text-[#E8B86D]" />
              <div>
                <span className="font-serif font-bold text-white text-lg leading-none block">
                  {categories.length > 1 ? categories.length - 1 : '4'}
                </span>
                <span className="text-[11px] uppercase tracking-wider text-white/80 font-bold">Categories</span>
              </div>
            </div>

            <div className="flex items-center gap-3 bg-[#7B1E3A]/90 hover:bg-[#7B1E3A] backdrop-blur-md text-white rounded-2xl px-4 py-3 border border-white/15 shadow-[0_8px_25px_rgba(0,0,0,0.3)] transition-all">
              <LuHeartHandshake className="w-6 h-6 text-[#E8B86D]" />
              <div>
                <span className="font-serif font-bold text-white text-lg leading-none block">1,200+</span>
                <span className="text-[11px] uppercase tracking-wider text-white/80 font-bold">Leaders</span>
              </div>
            </div>
          </div>

        </div>

        <div className="absolute bottom-0 left-0 right-0 w-full overflow-hidden leading-none z-20 pointer-events-none">
          <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="relative block w-full h-10 sm:h-12 text-[#FAF7F2] fill-current">
            <path d="M0,0 C150,90 350,-40 500,60 C650,160 900,10 1200,40 L1200,120 L0,120 Z"></path>
          </svg>
        </div>
      </section>

      {/* =========================================================
          2. INTERACTIVE SEARCH & CATEGORY FILTER TABS
      ========================================================= */}
      <section className="max-w-[1440px] mx-auto px-4 sm:px-8 pt-6 sm:pt-12 pb-4 sm:pb-6">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 sm:gap-6 bg-white rounded-[22px] sm:rounded-[28px] p-4 sm:p-6 border border-[#E8DDD7] shadow-[0_10px_35px_rgba(123,30,58,0.04)]">

          {/* Category Tabs (Mobile Horizontal Scroll) */}
          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar w-full md:w-auto pb-1.5 md:pb-0 scroll-smooth snap-x">
            {categories.map(cat => {
              const isSelected = selectedCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`px-3.5 py-2 sm:px-4 sm:py-2.5 rounded-full font-sans font-bold text-xs sm:text-[13px] transition-all duration-300 flex items-center gap-1.5 whitespace-nowrap shrink-0 snap-start cursor-pointer ${isSelected
                    ? 'bg-[#7B1E3A] text-white shadow-md scale-[1.02]'
                    : 'bg-[#FAF6F2] hover:bg-[#F2EAE5] text-[#7A6F6C] hover:text-[#7B1E3A] border border-[#E8DDD7]/70'
                    }`}
                >
                  <span>{cat.label}</span>
                  <span className={`text-[10px] sm:text-[11px] px-1.5 py-0.5 rounded-full ${isSelected ? 'bg-white/20 text-white' : 'bg-[#EAE1DF] text-[#7B1E3A]'
                    }`}>
                    {cat.count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Live Search Bar */}
          <div className="relative w-full md:w-80 lg:w-96 shrink-0">
            <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-[#A89F9B]">
              <LuSearch className="w-4 h-4" />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search departments or officials..."
              className="w-full pl-10 pr-9 py-2.5 sm:py-3 rounded-full sm:rounded-2xl bg-[#FAF6F2] border border-[#E8DDD7] text-[#4B2233] placeholder-[#A89F9B] text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-[#7B1E3A] focus:border-transparent transition-all duration-200"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-[#8B8683] hover:text-[#7B1E3A] transition-colors cursor-pointer"
              >
                <LuX className="w-4 h-4" />
              </button>
            )}
          </div>

        </div>

        {/* Active Filter Result Notice */}
        {searchQuery && (
          <div className="mt-4 px-2 flex items-center justify-between text-xs sm:text-sm text-[#8B8683]">
            <span>Showing results matching <strong>"{searchQuery}"</strong></span>
            <button
              onClick={() => { setSearchQuery(''); setSelectedCategory('all'); }}
              className="text-[#7B1E3A] hover:underline font-semibold cursor-pointer"
            >
              Reset All Filters
            </button>
          </div>
        )}
      </section>

      {/* =========================================================
          3. DEPARTMENTS DIRECTORY GRID (OPTIMIZED COMPACT LAYOUT)
      ========================================================= */}
      <section className="max-w-[1440px] mx-auto px-4 sm:px-8 py-4 sm:py-6 mb-12 sm:mb-20 space-y-6 sm:space-y-8">

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-[32px] border border-[#E8DDD7] shadow-sm">
            <LuLoader className="w-10 h-10 text-[#842343] animate-spin mb-3" />
            <p className="text-sm font-semibold text-[#5A4F4A]">Connecting to Firebase & loading departments...</p>
          </div>
        ) : filteredDepartments.length === 0 ? (
          <div className="bg-white rounded-[32px] p-12 border border-[#E8DDD7] text-center max-w-lg mx-auto my-8 shadow-sm">
            <div className="w-16 h-16 rounded-full bg-[#FAF6F2] border border-[#E8DDD7] text-[#8B8683] flex items-center justify-center mx-auto mb-4">
              <LuSearch className="w-7 h-7" />
            </div>
            <h3 className="font-serif font-bold text-[#7B1E3A] text-xl mb-2">No Departments Found</h3>
            <p className="text-[#5F5A58] text-sm mb-6">
              We couldn't find any department matching your current search criteria.
            </p>
            <button
              onClick={() => { setSearchQuery(''); setSelectedCategory('all'); }}
              className="px-6 py-2.5 rounded-xl bg-[#7B1E3A] text-white font-sans font-bold text-xs uppercase tracking-wider hover:bg-[#5E132A] transition-colors shadow-sm cursor-pointer"
            >
              View All Departments
            </button>
          </div>
        ) : (
          <>
            {/* ── A. FEATURED EXECUTIVE BANNER: BISHOP'S OFFICE HQ ── */}
            {featuredDept && (
              <div className="bg-gradient-to-br from-white via-white to-[#FAF6F2] rounded-[32px] p-6 sm:p-8 border-2 border-[#D4A64A]/70 shadow-[0_15px_45px_rgba(123,30,58,0.06)] relative overflow-hidden">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start relative z-10">

                  {/* Left Column: Identity & Direct Channels */}
                  <div className="lg:col-span-4 flex flex-col justify-between h-full space-y-5">
                    <div>
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-12 h-12 rounded-2xl bg-[#7B1E3A] text-[#D4A64A] flex items-center justify-center shadow-md shrink-0">
                          <GiHolySymbol className="w-6 h-6" />
                        </div>
                        <div>
                          <span className="text-[#D4A64A] font-sans font-bold text-[11px] uppercase tracking-[0.2em] block">
                            Diocesan Headquarters
                          </span>
                          <h2 className="text-[#7B1E3A] font-serif font-bold text-2xl sm:text-3xl">
                            {featuredDept.title}
                          </h2>
                        </div>
                      </div>
                      <p className="text-[#5F5A58] text-xs sm:text-sm leading-relaxed">
                        The central administration and episcopal secretariat overseeing all diocesan institutions, pastoral appointments, and official communications.
                      </p>
                    </div>

                    {/* Official Action Channels */}
                    <div className="pt-4 border-t border-[#E8DDD7]/80 space-y-2.5">
                      <span className="text-[11px] font-bold text-[#8A7E79] uppercase tracking-wider block">Official Channels</span>
                      <div className="flex flex-wrap gap-2">
                        {featuredDept.phoneNumber && (
                          <a
                            href={`tel:${featuredDept.phoneNumber}`}
                            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-[#FAF6F2] hover:bg-[#7B1E3A] text-[#7B1E3A] hover:text-white border border-[#E8DDD7] transition-all text-xs font-bold shadow-2xs"
                          >
                            <LuPhone className="w-3.5 h-3.5" />
                            <span>{featuredDept.phone || featuredDept.phoneNumber}</span>
                          </a>
                        )}

                        {featuredDept.whatsapp && (
                          <a
                            href={`https://wa.me/${featuredDept.whatsapp.replace(/[^0-9]/g, '')}`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-[#E9F9EE] hover:bg-[#25D366] text-[#25D366] hover:text-white border border-[#C5F0D2] transition-all text-xs font-bold shadow-2xs"
                          >
                            <FaWhatsapp className="w-3.5 h-3.5" />
                            <span>WhatsApp</span>
                          </a>
                        )}

                        {featuredDept.telegram && (
                          <a
                            href={`https://t.me/${featuredDept.telegram.replace(/[^0-9+]/g, '')}`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-[#E8F4FA] hover:bg-[#0088cc] text-[#0088cc] hover:text-white border border-[#C4E3F3] transition-all text-xs font-bold shadow-2xs"
                          >
                            <FaTelegramPlane className="w-3.5 h-3.5" />
                            <span>Telegram</span>
                          </a>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Right Column: 2-Column Clean Roster Grid */}
                  <div className="lg:col-span-8 bg-[#FAF6F2] rounded-[24px] p-5 sm:p-6 border border-[#E8DDD7]">
                    <h4 className="text-[#7B1E3A] font-serif font-bold text-base mb-3 flex items-center gap-2">
                      <LuUsers className="w-4 h-4 text-[#D4A64A]" />
                      <span>Executive Staff &amp; Officers</span>
                    </h4>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {featuredDept.dynamicFields.map((field, fIdx) => {
                        const phoneInside = extractPhoneNumber(field.value);
                        return (
                          <div key={fIdx} className="bg-white rounded-xl p-3 border border-[#E8DDD7]/70 shadow-2xs flex items-center justify-between gap-2">
                            <div>
                              <span className="text-[10px] font-bold text-[#D4A64A] uppercase tracking-wider block">
                                {field.label}
                              </span>
                              <p className="font-serif font-bold text-[#4B2233] text-xs sm:text-[13px] leading-tight mt-0.5">
                                {field.value}
                              </p>
                            </div>
                            {phoneInside && (
                              <a
                                href={`tel:${phoneInside}`}
                                className="shrink-0 p-1.5 rounded-lg bg-[#FAF6F2] hover:bg-[#7B1E3A] text-[#7B1E3A] hover:text-white transition-colors"
                                title={`Call ${phoneInside}`}
                              >
                                <LuPhone className="w-3 h-3" />
                              </a>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                </div>
              </div>
            )}

            {/* ── B. STANDARD COMPACT DEPARTMENTS GRID (NATURAL HEIGHT) ── */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-start">
              {standardDepartments.map((dept) => {
                const IconComponent = dept.icon || LuBuilding2;

                return (
                  <div
                    key={dept.id}
                    className="bg-white rounded-[24px] p-5 sm:p-6 border border-[#E8DDD7] shadow-[0_4px_20px_rgba(123,30,58,0.03)] hover:shadow-[0_12px_30px_rgba(123,30,58,0.09)] transition-all duration-300 flex flex-col justify-between group hover:-translate-y-1"
                  >
                    {/* Header */}
                    <div>
                      <div className="flex items-center justify-between gap-2 mb-3">
                        <div className="w-10 h-10 rounded-xl bg-[#FDF3F0] text-[#7B1E3A] border border-[#F0D1C8] flex items-center justify-center shadow-2xs group-hover:bg-[#7B1E3A] group-hover:text-[#D4A64A] transition-colors shrink-0">
                          <IconComponent className="w-5 h-5" />
                        </div>
                        <span className="text-[#D4A64A] font-sans font-bold text-[10px] uppercase tracking-[0.15em] bg-[#FAF6F2] px-2.5 py-1 rounded-full border border-[#E8DDD7]/70">
                          {dept.badge}
                        </span>
                      </div>

                      <h3 className="text-[#7B1E3A] font-serif font-bold text-lg leading-snug mb-3 group-hover:text-[#5E132A] transition-colors">
                        {dept.title}
                      </h3>

                      {/* Compact Dynamic Fields List */}
                      {dept.dynamicFields.length > 0 && (
                        <div className="space-y-2 mb-4">
                          {dept.dynamicFields.map((field, idx) => {
                            if (field.isAddress) {
                              return (
                                <div key={idx} className="bg-[#FAF6F2] p-2.5 rounded-xl border border-[#E8DDD7]/60 flex items-start gap-2 text-xs">
                                  <LuMapPin className="w-3.5 h-3.5 text-[#7B1E3A] shrink-0 mt-0.5" />
                                  <span className="text-[#5F5A58] leading-relaxed text-[11px] sm:text-xs">{field.value}</span>
                                </div>
                              );
                            }

                            if (field.isEmail) {
                              return (
                                <div key={idx} className="flex items-center justify-between gap-2 text-xs bg-[#FAF8F6] p-2.5 rounded-xl border border-[#E8DDD7]/50">
                                  <div>
                                    <span className="text-[#8A7E79] text-[9px] font-bold uppercase tracking-wider block">{field.label}</span>
                                    <a href={`mailto:${field.value}`} className="font-semibold text-[#7B1E3A] hover:underline text-[11px] break-all">
                                      {field.value}
                                    </a>
                                  </div>
                                  <LuMail className="w-3.5 h-3.5 text-[#D4A64A] shrink-0" />
                                </div>
                              );
                            }

                            const phoneInside = extractPhoneNumber(field.value);

                            return (
                              <div key={idx} className="bg-[#FAF8F6] p-2.5 rounded-xl border border-[#E8DDD7]/50 text-xs">
                                <span className="text-[#8A7E79] text-[9px] font-bold uppercase tracking-wider block mb-0.5">
                                  {field.label}
                                </span>
                                <div className="flex items-start justify-between gap-1.5">
                                  <p className="font-semibold text-[#2D2420] text-[11px] sm:text-xs leading-snug">{field.value}</p>
                                  {phoneInside && (
                                    <a
                                      href={`tel:${phoneInside}`}
                                      className="shrink-0 p-1 rounded-md bg-[#FAF6F2] hover:bg-[#7B1E3A] text-[#7B1E3A] hover:text-white transition-colors"
                                      title={`Call ${phoneInside}`}
                                    >
                                      <LuPhone className="w-2.5 h-2.5" />
                                    </a>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Compact Team Badges */}
                      {dept.team.length > 0 && (
                        <div className="mb-4 bg-[#FAF6F2] p-2.5 rounded-xl border border-[#E8DDD7]/60">
                          <span className="text-[9px] font-bold text-[#8A7E79] uppercase tracking-wider block mb-1.5">
                            Team
                          </span>
                          <div className="flex flex-wrap gap-1">
                            {dept.team.map((member, mIdx) => (
                              <span key={mIdx} className="inline-flex items-center gap-1 bg-white text-[#2D2420] text-[10px] font-semibold px-2 py-0.5 rounded-lg border border-[#E8DDD7]/60 shadow-2xs">
                                <LuUser className="w-2.5 h-2.5 text-[#D4A64A]" />
                                <span>{member}</span>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Compact Footer Actions */}
                    <div className="pt-3 border-t border-[#F5F1EE] flex items-center justify-between gap-2 text-xs">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {dept.phoneNumber && (
                          <a
                            href={`tel:${dept.phoneNumber}`}
                            className="inline-flex items-center gap-1 font-sans font-bold text-[#7B1E3A] hover:text-[#5E132A] transition-colors bg-[#FDF3F0] px-2.5 py-1 rounded-lg border border-[#F0D1C8] text-[11px]"
                          >
                            <LuPhone className="w-3 h-3" />
                            <span>Call</span>
                          </a>
                        )}

                        {dept.email && (
                          <a
                            href={`mailto:${dept.email}`}
                            className="inline-flex items-center gap-1 font-sans font-bold text-[#8A7E79] hover:text-[#7B1E3A] transition-colors bg-[#FAF6F2] px-2.5 py-1 rounded-lg border border-[#E8DDD7] text-[11px]"
                          >
                            <LuMail className="w-3 h-3" />
                            <span>Email</span>
                          </a>
                        )}
                      </div>

                      {dept.address && (
                        <a
                          href={`https://maps.google.com/?q=${encodeURIComponent(dept.title + ' ' + dept.address)}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-[#8A7E79] hover:text-[#7B1E3A] font-semibold text-[11px] py-1 px-1.5 transition-colors ml-auto"
                        >
                          <span>Map</span>
                          <LuExternalLink className="w-2.5 h-2.5" />
                        </a>
                      )}
                    </div>

                  </div>
                );
              })}
            </div>
          </>
        )}

      </section>

      {/* =========================================================
          4. DIOCESAN INQUIRY & SUPPORT CTA BANNER
      ========================================================= */}
      <section className="max-w-[1440px] mx-auto px-4 sm:px-8 pb-12 sm:pb-20">
        <div className="bg-gradient-to-r from-[#842343] via-[#6c1b35] to-[#4c1224] rounded-[28px] sm:rounded-[36px] p-6 sm:p-12 lg:p-14 text-white shadow-[0_20px_60px_rgba(132,35,67,0.3)] border border-white/20 relative overflow-hidden flex flex-col md:flex-row items-center justify-between gap-6 sm:gap-8 text-center md:text-left">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#E8B86D]/20 rounded-full blur-3xl pointer-events-none"></div>

          <div className="max-w-2xl relative z-10">
            <span className="inline-block bg-[#E8B86D]/20 text-[#E8B86D] border border-[#E8B86D]/40 px-4 py-1.5 rounded-full font-sans font-extrabold text-xs uppercase tracking-[0.2em] mb-3 shadow-xs">
              NEED ASSISTANCE?
            </span>
            <h2 className="font-serif font-bold text-3xl sm:text-4xl lg:text-[40px] text-white leading-tight mb-4 drop-shadow-sm">
              Connect With Our Diocesan Headquarters
            </h2>
            <p className="text-white/95 text-base sm:text-[17px] leading-relaxed font-sans font-normal drop-shadow-xs">
              For general inquiries, official documentation, pastoral appointments, or fellowship registrations, reach out directly to our central administrative desk at Bank Road, Calicut.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 shrink-0 relative z-10 w-full md:w-auto justify-center">
            <a
              href="tel:04952721748"
              className="px-8 py-4 rounded-2xl bg-[#E8B86D] hover:bg-[#D4A64A] text-[#1A1A1A] font-sans font-extrabold text-xs sm:text-sm uppercase tracking-wider transition-all duration-300 shadow-md hover:shadow-xl inline-flex items-center justify-center gap-2"
            >
              <LuPhone className="w-4 h-4 text-[#842343]" />
              <span>Call Diocesan Office</span>
            </a>
            <button
              onClick={() => onSelectSection('about')}
              className="px-8 py-4 rounded-2xl bg-white/15 hover:bg-white/25 text-white font-sans font-bold text-xs sm:text-sm uppercase tracking-wider transition-all duration-300 border border-white/30 inline-flex items-center justify-center gap-2 cursor-pointer shadow-xs"
            >
              <span>Diocesan Leadership</span>
              <LuArrowRight className="w-4 h-4 text-[#E8B86D]" />
            </button>
          </div>
        </div>
      </section>

    </div>
  );
}
